import { useMutation } from '@apollo/client';
import { Form, Formik } from 'formik';
import { Button } from '~/components/ui/button';
import { toast } from 'react-toastify';
import type {
  UpdateLithostratMemberMutation,
  UpdateLithostratMemberMutationVariables,
} from '~/apollo/generated/v3/graphql';
import { UPDATE_LITHOSTRAT_MEMBER } from '~/apollo/operations/lithostrat';
import { FormErrors } from '~/components/common/FormErrors';
import { Panel } from '~/components/common/Panel';
import { LithostratAgeFormFields } from '~/components/upload/lithostratAge/LithostratAgeFormFields';
import { useMemberUpdateOutletContext } from '~/routes/upload/lithostrat/member/$memberId';
import {
  formValuesToLithostratMemberInput,
  initialMember,
} from '~/utils/modules/lithostrat';
import type { LithostratAgeFormValues } from '~/utils/modules/lithostratAge';
import { initialLithostratAge } from '~/utils/modules/lithostratAge';

type FormValues = {
  startAge: LithostratAgeFormValues;
  endAge: LithostratAgeFormValues;
};

export default function MemberUpdateGeologicalAgeRoute() {
  const { member, refetchQueries } = useMemberUpdateOutletContext();

  const [updateMember, { loading, error }] = useMutation<
    UpdateLithostratMemberMutation,
    UpdateLithostratMemberMutationVariables
  >(UPDATE_LITHOSTRAT_MEMBER, {
    refetchQueries,
  });

  async function handleSubmit(values: FormValues) {
    const memberFormValues = initialMember(member);
    const memberInput = {
      ...formValuesToLithostratMemberInput(memberFormValues),
      startAge: values.startAge,
      endAge: values.endAge,
    };

    try {
      await updateMember({
        variables: {
          id: member.id,
          member: memberInput,
        },
      });
      toast.success('Member updated successfully.');
    } catch (err) {
      console.log('Error updating member', err);
      toast.error('There was a problem updating the member. Please try again.');
    }
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={{
        startAge: initialLithostratAge(member.startAge),
        endAge: initialLithostratAge(member.endAge),
      }}
    >
      <Form className="space-y-4">
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <Panel>
              <Panel.Heading>
                <Panel.Title>Start Age</Panel.Title>
              </Panel.Heading>
              <Panel.Body>
                <LithostratAgeFormFields ageName="startAge" />
              </Panel.Body>
            </Panel>
          </div>

          <div>
            <Panel>
              <Panel.Heading>
                <Panel.Title>End Age</Panel.Title>
              </Panel.Heading>
              <Panel.Body>
                <LithostratAgeFormFields ageName="endAge" />
              </Panel.Body>
            </Panel>
          </div>
        </div>

        <FormErrors graphQLError={error} />

        <div className="text-center">
          <Button type="submit" color="primary" loading={loading}>
            Save
          </Button>
        </div>
      </Form>
    </Formik>
  );
}
